export const TAB_SKINS = Object.freeze(['default', 'wireframe']);

import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/tab.default.js';
import * as wireframeSkin from './skins/tab.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunTab extends RunElement {
  #panel = '';
  #active = false;
  #disabled = false;
  #closable = false;
  #label = '';
  #icon = '';
  #badge = undefined;
  #skin = null;
  #rendering = false;

  static get observedAttributes() {
    return ['panel', 'active', 'disabled', 'closable', 'skin'];
  }

  // Arrow-function closures — private methods are not writable;
  // never use this.#method = this.#method.bind(this).
  _onClickBound = (e) => this.#handleClick(e);
  _onKeydownBound = (e) => this.#handleKeydown(e);
  _onSlotChangeBound = (e) => this.#handleSlotChange(e);
  #handleCloseBound = (e) => {
    e.stopPropagation();
    if (this.#disabled) return;
    this.#emitTabClose();
  };

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'tab');
    this.addEventListener('click', this._onClickBound);
    this.addEventListener('keydown', this._onKeydownBound);
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onClickBound);
    this.removeEventListener('keydown', this._onKeydownBound);
    if (this.shadowRoot) {
      this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
      });
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'panel':    this.panel = newValue !== null ? newValue : ''; break;
      case 'active':   this.active = newValue !== null; break;
      case 'disabled': this.disabled = newValue !== null; break;
      case 'closable': this.closable = newValue !== null; break;
      case 'skin':     this.skin = newValue; break;
    }
  }

  get panel() { return this.#panel; }
  set panel(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#panel === coerced) return;
    this.#panel = coerced;
    if (coerced) {
      this.setAttribute('panel', coerced);
    } else {
      this.removeAttribute('panel');
    }
    this.#updateAria();
    this.requestUpdate();
  }

  get active() { return this.#active; }
  set active(value) {
    const coerced = Boolean(value);
    if (this.#active === coerced) return;
    this.#active = coerced;
    this.toggleAttribute('active', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get disabled() { return this.#disabled; }
  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    // Do NOT reflect to native `disabled` attribute — tabs must remain focusable
    // when disabled. aria-disabled is the correct ARIA signal (set in #updateAria).
    this.#updateAria();
    this.#propagateDisabled(coerced);
    this.requestUpdate();
  }

  get closable() { return this.#closable; }
  set closable(value) {
    const coerced = Boolean(value);
    if (this.#closable === coerced) return;
    this.#closable = coerced;
    this.toggleAttribute('closable', coerced);
    this.requestUpdate();
  }

  get label() { return this.#label; }
  set label(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#label === coerced) return;
    this.#label = coerced;
    this.requestUpdate();
  }

  get icon() { return this.#icon; }
  set icon(value) {
    const coerced = String(value == null ? '' : value);
    if (this.#icon === coerced) return;
    this.#icon = coerced;
    this.requestUpdate();
  }

  get badge() { return this.#badge; }
  set badge(value) {
    if (this.#badge === value) return;
    this.#badge = value;
    this.requestUpdate();
  }

  get skin() { return this.#skin; }
  set skin(value) {
    const coerced = value != null ? String(value).trim() : null;
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced) {
      this.setAttribute('skin', coerced);
    } else {
      this.removeAttribute('skin');
    }
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
      this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
        slot.addEventListener('slotchange', this._onSlotChangeBound);
      });
      const closeBtn = this.shadowRoot.querySelector('[part="close-trigger"]');
      if (closeBtn) {
        closeBtn.addEventListener('click', this.#handleCloseBound);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  firstUpdated(changedProperties) {
    super.firstUpdated?.(changedProperties);
    if (this.shadowRoot) {
      const defaultSlot = this.shadowRoot.querySelector('slot:not([name])');
      if (defaultSlot) {
        defaultSlot.removeEventListener('slotchange', this._onSlotChangeBound);
        defaultSlot.addEventListener('slotchange', this._onSlotChangeBound);
      }
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    this.setAttribute('aria-selected', String(this.#active));
    if (this.#disabled) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }
    if (this.#panel) {
      this.setAttribute('aria-controls', this.#panel);
    } else {
      this.removeAttribute('aria-controls');
    }
    this.setAttribute('tabindex', this.#disabled ? '-1' : '0');
  }

  #handleSlotChange(event) {
    const slot = event.target;
    if (!slot) return;
    const slotName = slot.getAttribute('name');
    const assigned = slot.assignedElements({ flatten: true });
    if (slotName) {
      this.toggleAttribute(`data-has-${slotName}`, assigned.length > 0);
      assigned.forEach(el => {
        if ('disabled' in el) el.disabled = this.#disabled;
        // NEVER propagate `name` to child elements.
      });
    }
  }

  #propagateDisabled(disabled) {
    if (!this.shadowRoot) return;
    this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
      slot.assignedElements({ flatten: true }).forEach(el => {
        if ('disabled' in el) el.disabled = disabled;
      });
    });
  }

  #handleClick(event) {
    if (this.#disabled) return;
    this.#activate();
  }

  #handleKeydown(event) {
    if (this.#disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#activate();
    }
  }

  #activate() {
    if (this.#active) return;
    this.#emitTabSelect();
  }

  #emitTabSelect() {
    this.dispatchEvent(new CustomEvent('tab-select', {
      bubbles: true,
      composed: true,
      detail: { panel: this.#panel },
    }));
  }

  #emitTabClose() {
    this.dispatchEvent(new CustomEvent('tab-close', {
      bubbles: true,
      composed: true,
      detail: { panel: this.#panel },
    }));
  }
}

if (!customElements.get('run-tab')) {
  customElements.define('run-tab', RunTab);
}

export { RunTab };
export default RunTab;
